import { Component, ElementRef, HostListener, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router, RouterModule } from '@angular/router';
import { COUNTRY_CODE_OPTIONS } from '../country-code-options';
import { LoginService } from '../services/login.service';
import { AuthService, UserProfile } from '../services/auth.service';
import { LoginModalService } from '../services/login-modal.service';
import { LocationUpdateModalService } from '../services/location-update-modal.service';
import { DataService } from '../services/data.service';
import { CityService } from '../services/city.service';
import { CartService } from '../services/cart.service';
import { SeatHoldReleaseService } from '../services/seat-hold-release.service';
import { WishlistService } from '../services/wishlist.service';
import { ThemeService } from '../services/theme.service';
import { CarouselComponent } from '../carousel/carousel.component';
import { buildEventRouteFromInput } from '../utils/event-navigation.util';
import { getEventPortraitImageUrl } from '../utils/event-image.util';
import { ToastModule } from 'primeng/toast';
import { ButtonModule } from 'primeng/button';
import { MessageService } from 'primeng/api';

interface HeaderSearchSuggestion {
  title: string;
  category: string;
  city: string;
  date: string;
  time: string;
  thumbnailImage?: string | null;
  thumbnailLabel: string;
  thumbnailGradient: string;
}

interface HeaderSearchResult extends HeaderSearchSuggestion {
  id: string;
  kind: 'event';
  raw: Record<string, unknown>;
  event_status: string
}

const HEADER_SEARCH_ALL_TAB = 'All';
const HEADER_CATEGORY_PAGE_SIZE = 50;

@Component({
  selector: 'app-header',
  standalone: true,
  imports: [CommonModule, FormsModule, RouterModule, ToastModule, ButtonModule],
  templateUrl: './header.component.html',
  styleUrl: './header.component.css'
})
export class HeaderComponent implements OnInit, OnDestroy {
  @ViewChild('searchTabsStrip') searchTabsStripRef?: ElementRef<HTMLDivElement>;

  selectedCity = 'Choose city';
  cityPickerModalOpen = false;
  searchModalOpen = false;
  scrolled = false;
  loginModalOpen = false;
  locationUpdateModalOpen = false;
  logoutModalOpen = false;
  locationUpdateFrom = '';
  locationUpdateTo = '';
  mobileNumber = '';
  selectedCountryId = 1;
  readonly countryCodeOptions = COUNTRY_CODE_OPTIONS.map(country => ({
    ...country,
    // Some source entries include an area prefix or alternate prefixes.
    // Keep the calling code separate; the phone field contains the national number.
    dialCode: `+${country.phonecode.split(/[- ]/)[0].replace(/\D/g, '').replace(/^00/, '').replace(/^1721$/, '1')}`
  }));

  get selectedCountryCode(): string {
    return this.countryCodeOptions.find(country => country.id === this.selectedCountryId)?.dialCode ?? '';
  }

  get mobileNumberLengths(): readonly number[] | undefined {
    // National mobile-number lengths, excluding calling code and trunk prefix.
    const lengths: Record<string, readonly number[]> = {
      '+91': [10], '+1': [10], '+44': [10], '+61': [9],
      '+65': [8], '+971': [9], '+33': [9], '+94': [9],
      '+977': [10], '+92': [10], '+880': [10], '+852': [8]
    };
    return lengths[this.selectedCountryCode];
  }

  get mobileNumberValidationMessage(): string {
    if (!/^[0-9]*$/.test(this.mobileNumber)) {
      return 'Please enter digits only (0-9).';
    }
    const lengths = this.mobileNumberLengths;
    return lengths
      ? `Please enter a valid ${lengths.join(' or ')}-digit mobile number for ${this.selectedCountryCode}.`
      : 'Please enter a mobile number containing 7-15 digits.';
  }

  get isMobileNumberValid(): boolean {
    if (!this.selectedCountryCode || !/^[0-9]+$/.test(this.mobileNumber)) return false;
    return this.mobileNumberLengths
      ? this.mobileNumberLengths.includes(this.mobileNumber.length)
      : this.mobileNumber.length >= 7 && this.mobileNumber.length <= 15;
  }

  onCountryCodeChange(): void {
    this.errorMessage = this.mobileNumber && !this.isMobileNumberValid
      ? this.mobileNumberValidationMessage : '';
    this.successMessage = '';
  }
  navbarSearchQuery = '';
  activeSearchTab = HEADER_SEARCH_ALL_TAB;
  isLoading = false;
  isSearchModalLoading = false;
  errorMessage = '';
  successMessage = '';
  searchModalError = '';
  searchModalResults: HeaderSearchResult[] = [];

  private selectedCitySub: any;
  private cityPickerSub: any;
  private locationUpdateSub: any;
  private wishlistAuthSub: any;
  private searchModalDebounceTimer: ReturnType<typeof setTimeout> | null = null;

  searchTabs: string[] = [HEADER_SEARCH_ALL_TAB];
  readonly trendingSearchSuggestions: HeaderSearchSuggestion[] = [
    {
      title: 'Palliyezhuchi Paavai Paadalgal',
      category: 'Expos',
      city: 'Delhi NCR',
      date: 'Jul 27, 2026',
      time: '6:30 PM',
      thumbnailLabel: 'PP',
      thumbnailGradient: 'linear-gradient(135deg, #f97316 0%, #ec4899 100%)'
    },
    {
      title: 'Coldplay Music Of The Spheres World Tour 2026',
      category: 'Concerts',
      city: 'Delhi NCR',
      date: 'Nov 25, 2026',
      time: '5:35 PM',
      thumbnailLabel: 'CM',
      thumbnailGradient: 'linear-gradient(135deg, #2563eb 0%, #7c3aed 100%)'
    },
    {
      title: 'Future Tech Conference 2026',
      category: 'Conferences',
      city: 'Delhi NCR',
      date: 'Sep 10, 2026',
      time: '9:00 AM',
      thumbnailLabel: 'FT',
      thumbnailGradient: 'linear-gradient(135deg, #0f766e 0%, #06b6d4 100%)'
    },
    {
      title: 'Comicstaan Live',
      category: 'Activities',
      city: 'Delhi NCR',
      date: 'Aug 05, 2026',
      time: '7:00 PM',
      thumbnailLabel: 'CL',
      thumbnailGradient: 'linear-gradient(135deg, #f59e0b 0%, #ef4444 100%)'
    },
    {
      title: 'Mere Krishna | A Live Theatrical Experience',
      category: 'Plays',
      city: 'Delhi NCR',
      date: 'Jul 30, 2026',
      time: '7:30 PM',
      thumbnailLabel: 'MK',
      thumbnailGradient: 'linear-gradient(135deg, #10b981 0%, #14b8a6 100%)'
    },
    {
      title: 'Delhi Capitals vs Mumbai Indians',
      category: 'Sports',
      city: 'Delhi NCR',
      date: 'Aug 12, 2026',
      time: '7:30 PM',
      thumbnailLabel: 'DC',
      thumbnailGradient: 'linear-gradient(135deg, #1d4ed8 0%, #dc2626 100%)'
    },
    {
      title: 'Football Finale Live Screening at Select Citywalk',
      category: 'Sports',
      city: 'Delhi NCR',
      date: 'May 31, 2026',
      time: '8:00 PM',
      thumbnailLabel: 'FF',
      thumbnailGradient: 'linear-gradient(135deg, #0f172a 0%, #2563eb 100%)'
    },
    {
      title: 'Bollywood Retro Night',
      category: 'Concerts',
      city: 'Delhi NCR',
      date: 'Jun 14, 2026',
      time: '8:00 PM',
      thumbnailLabel: 'BR',
      thumbnailGradient: 'linear-gradient(135deg, #7f1d1d 0%, #db2777 100%)'
    },
    {
      title: 'Inception Re-Release',
      category: 'Movies',
      city: 'Delhi NCR',
      date: 'Jul 19, 2026',
      time: '3:00 PM',
      thumbnailLabel: 'IN',
      thumbnailGradient: 'linear-gradient(135deg, #374151 0%, #111827 100%)'
    },
    {
      title: 'Delhi Food & Music Festival',
      category: 'Activities',
      city: 'Delhi NCR',
      date: 'Aug 22, 2026',
      time: '4:00 PM',
      thumbnailLabel: 'DF',
      thumbnailGradient: 'linear-gradient(135deg, #b45309 0%, #fb7185 100%)'
    }
  ];

  // Categories (for navigation)
  categories: any[] = [];

  headerNavItems: Array<{
    label: string;
    kind: 'discover' | 'category' | 'link';
    /** For kind=category, used to resolve category_id / query param */
    categoryName?: string;
    /** For kind=link, when API provides a URL */
    href?: string;
  }> = [
      { label: 'Discover', kind: 'discover' },
      { label: 'Concerts', kind: 'category', categoryName: 'Concerts' },
      { label: 'Expos', kind: 'category', categoryName: 'Expos' },
      { label: 'Conferences', kind: 'category', categoryName: 'Conferences' }
    ];

  // OTP Verification
  otpSent = false;
  otpVerificationToken = '';
  otpExpiry = 0;
  otpDigits = ['', '', '', '']; // 4-digit OTP
  otpTimer = 0;
  /** True after countdown hits 0 until user resends or changes number */
  otpExpired = false;
  timerInterval: any;
  otpIndices = [0, 1, 2, 3]; // Array of indices for tracking (4 digits)

  // User Profile
  userProfile: UserProfile | null = null;
  userDropdownOpen = false;
  mobileMenuOpen = false;

  cartHasItems = false;
  hideHeaderCart = false;
  private cartStateSub: any;
  private hideHeaderCartSub: any;
  reviewCountdown = '';
  reviewCountdownDanger = false;
  private reviewTimerInterval: any;
  private reviewExpiryHandled = false;
  isLoggedIn = false;

  /** Mobile OTP (default) or email/password; swap via in-modal links */
  loginModalStep: 'mobile' | 'email' = 'mobile';
  loginEmail = '';
  loginPassword = '';

  constructor(
    private loginService: LoginService,
    private authService: AuthService,
    private dataService: DataService,
    private cityService: CityService,
    private cartService: CartService,
    private seatHoldRelease: SeatHoldReleaseService,
    private router: Router,
    public loginModalService: LoginModalService,
    private locationUpdateModalService: LocationUpdateModalService,
    private wishlistService: WishlistService,
    private themeService: ThemeService,
    private ms: MessageService
  ) { }

  get isDarkMode(): boolean {
    return this.themeService.isDarkMode();
  }

  get themeToggleLabel(): string {
    return this.isDarkMode ? 'Day mode' : 'Night mode';
  }

  toggleTheme(): void {
    this.themeService.toggleTheme();
  }

  openReviewFromCartClick(ev: Event): void {
    ev.preventDefault();
    this.closeUserDropdown();
    void this.router.navigate(['/cart']);
  }

  openSavedDevices(): void {
    this.closeUserDropdown();
    void this.router.navigate(['/profile'], { queryParams: { tab: 'savedDevices' } });
  }

  openProfileTab(tab: 'profile' | 'orders' | 'wishlist' | 'notifications' | 'settings' | 'savedDevices'): void {
    this.closeUserDropdown();
    void this.router.navigate(['/profile'], { queryParams: { tab } });
  }

  ngOnInit() {
    this.loginModalService.openModal$.subscribe(() => {
      this.openLoginModal();
    });
    // React to auth state changes (e.g. logout from profile page)
    this.authService.authState$.subscribe((profile) => {
      this.userProfile = profile;
      this.isLoggedIn = !!profile || this.authService.isAuthenticated();
      // After successful login, token may exist before profile is fetched.
      // Ensure we fetch profile so header doesn't show "Sign In".
      if (this.isLoggedIn && !profile) {
        this.loadUserProfile();
      }
    });

    this.wishlistAuthSub = this.authService.authState$.subscribe(() => {
      this.syncWishlistRefs();
    });

    this.cartStateSub = this.cartService.cartHasItems$.subscribe((has) => {
      this.cartHasItems = !!has;
    });
    this.hideHeaderCartSub = this.cartService.hideHeaderCart$.subscribe((hide) => {
      this.hideHeaderCart = !!hide;
    });

    // Initialize cart state when opening app (logged in only).
    if (this.authService.isAuthenticated()) {
      this.isLoggedIn = true;
      this.cartService.getCart().subscribe({ next: () => { }, error: () => { } });
    }

    this.startReviewCountdownTicker();
    // Load cities from API (shared service, dynamic only - no static fallback)
    this.cityService.loadCities();
    this.selectedCitySub = this.cityService.selectedCity$.subscribe((s) => {
      this.selectedCity = s?.name ?? 'Choose city';
    });
    this.cityPickerSub = this.cityService.cityPickerOpen$.subscribe((open) => {
      this.cityPickerModalOpen = open;
    });
    this.locationUpdateSub = this.locationUpdateModalService.show$.subscribe((details) => {
      this.openLocationUpdateModal(details.fromCity, details.toCity);
    });

    // Load categories for navigation
    this.loadCategories();

    // Load navbar (encrypted; auto-decrypted by interceptor)
    this.loadNavbar();

    // Load user profile if authenticated
    if (this.authService.isAuthenticated()) {
      // Try to load from localStorage first
      const savedProfile = this.authService.getUserProfile();
      if (savedProfile) {
        this.userProfile = savedProfile;
      }
      // Always fetch fresh profile from API
      this.loadUserProfile();
    }
    this.syncWishlistRefs();
  }

  /** First 2 letters of city for responsive header */
  getCityShort(): string {
    const name = (this.selectedCity || '').trim();
    if (!name || name === 'Choose city') return '—';
    if (name.length <= 2) return name;
    return name.slice(0, 2);
  }

  @HostListener('window:scroll', [])
  onWindowScroll() {
    this.scrolled = window.scrollY > 50;
  }

  @HostListener('window:resize')
  onWindowResize(): void {
    if (window.innerWidth <= 1023 && this.searchModalOpen) {
      this.closeSearchModal();
    }
  }

  @HostListener('document:click', ['$event'])
  onClickOutside(event: MouseEvent) {
    const target = event.target as HTMLElement;
    if (!target.closest('#userBtn') && !target.closest('.user-dropdown')) {
      this.userDropdownOpen = false;
    }
    if (!target.closest('#menuToggle') && !target.closest('.mobile-menu-drawer') && !target.closest('.mobile-menu-close')) {
      this.mobileMenuOpen = false;
    }
  }

  @HostListener('document:keydown.escape', ['$event'])
  onEscapeKey(event: Event) {
    if (!(event instanceof KeyboardEvent)) return;
    if (this.searchModalOpen) {
      this.closeSearchModal();
    }
    if (this.loginModalOpen) {
      this.closeLoginModal();
    }
    if (this.locationUpdateModalOpen) {
      this.closeLocationUpdateModal();
    }
    if (this.mobileMenuOpen) {
      this.closeMobileMenu();
    }
  }

  ngOnDestroy() {
    this.selectedCitySub?.unsubscribe();
    this.cityPickerSub?.unsubscribe();
    this.locationUpdateSub?.unsubscribe();
    this.cartStateSub?.unsubscribe();
    this.hideHeaderCartSub?.unsubscribe();
    this.wishlistAuthSub?.unsubscribe();
    if (this.reviewTimerInterval) {
      clearInterval(this.reviewTimerInterval);
      this.reviewTimerInterval = null;
    }
    if (this.loginModalOpen || this.locationUpdateModalOpen) {
      document.body.classList.remove('modal-open');
    }
    document.body.classList.remove('mobile-menu-open');
    this.clearTimer();
    this.clearSearchModalDebounce();
  }

  openCityPickerModal(): void {
    this.cityService.openCityPicker();
  }

  get filteredSearchSuggestions(): HeaderSearchResult[] {
    if (this.navbarSearchQuery.trim().length < 3) {
      return this.searchModalResults;
    }
    return this.searchModalResults.filter((item) => this.matchesSearchTab(item, this.activeSearchTab));
  }

  openSearchModal(): void {
    if (window.innerWidth <= 1023) {
      return;
    }
    this.searchModalOpen = true;
    this.userDropdownOpen = false;
    this.mobileMenuOpen = false;
    document.body.classList.remove('mobile-menu-open');
    this.syncBodyModalScrollLock();
    this.loadSearchModalResults(true);
    setTimeout(() => {
      const el = document.getElementById('navbar-search-input') as HTMLInputElement | null;
      el?.focus();
    }, 50);
  }

  closeSearchModal(): void {
    this.searchModalOpen = false;
    this.navbarSearchQuery = '';
    this.activeSearchTab = HEADER_SEARCH_ALL_TAB;
    this.searchModalError = '';
    this.searchModalResults = [];
    this.clearSearchModalDebounce();
    this.syncBodyModalScrollLock();
  }

  setActiveSearchTab(tab: string): void {
    this.activeSearchTab = tab;
    this.loadSearchModalResults(true);
  }

  onSearchTabsWheel(event: WheelEvent): void {
    const strip = this.searchTabsStripRef?.nativeElement;
    if (!strip) {
      return;
    }

    const hasHorizontalOverflow = strip.scrollWidth > strip.clientWidth;
    if (!hasHorizontalOverflow) {
      return;
    }

    const horizontalDelta =
      Math.abs(event.deltaX) > Math.abs(event.deltaY) ? event.deltaX : event.deltaY;

    if (horizontalDelta === 0) {
      return;
    }

    strip.scrollLeft += horizontalDelta;
    event.preventDefault();
  }

  onNavbarSearchQueryChange(value: string): void {
    this.navbarSearchQuery = value;
    this.searchModalError = '';
    this.clearSearchModalDebounce();

    if (!this.searchModalOpen) {
      return;
    }

    const query = value.trim();
    if (!query) {
      this.loadSearchModalResults(true);
      return;
    }

    if (query.length < 3) {
      this.loadCategoryModalResults();
      return;
    }

    this.searchModalDebounceTimer = setTimeout(() => {
      this.loadSearchModalResults(true);
    }, 300);
  }

  onSearchResultClick(item: HeaderSearchResult): void {
    this.closeSearchModal();

    if (item.kind === 'event') {
      const route = buildEventRouteFromInput(item.raw);
      if (route) {
        void this.router.navigate(route);
        return;
      }
    }

    void this.router.navigate(['/discover'], {
      queryParams: {
        q: item.title,
        city: this.selectedCity !== 'Choose city' ? this.selectedCity.toLowerCase() : null
      }
    });
  }

  onSearchResultKeydown(event: KeyboardEvent, item: HeaderSearchResult): void {
    if (event.key !== 'Enter' && event.key !== ' ') {
      return;
    }
    event.preventDefault();
    this.onSearchResultClick(item);
  }

  isSearchResultWishlisted(item: HeaderSearchResult): boolean {
    return this.wishlistService.isWishlisted(this.wishlistService.extractReferenceNo(item.raw));
  }

  onSearchWishlistClick(item: HeaderSearchResult, domEvent: MouseEvent): void {
    domEvent.preventDefault();
    domEvent.stopPropagation();

    const referenceNo = this.wishlistService.extractReferenceNo(item.raw);
    if (!referenceNo) {
      this.ms.add({
        severity: 'error',
        summary: 'Wishlist',
        detail: 'Unable to save this event right now.',
        life: 3000
      });
      return;
    }

    if (this.wishlistService.isWishlisted(referenceNo)) {
      this.removeSearchResultFromWishlist(referenceNo);
      return;
    }

    if (!this.authService.isAuthenticated()) {
      this.loginModalService.requestLogin(() => {
        this.addSearchResultToWishlist(referenceNo);
      });
      return;
    }

    this.addSearchResultToWishlist(referenceNo);
  }

  get searchModalHeading(): string {
    const label = this.selectedCity === 'Choose city' ? 'Your City' : this.selectedCity;
    const query = this.navbarSearchQuery.trim();
    if (query.length >= 3) {
      return `Results for "${query}"`;
    }
    if (this.activeSearchTab !== HEADER_SEARCH_ALL_TAB) {
      return `Top 10 ${this.activeSearchTab} Events`;
    }
    return `Top 10 Popular Events${label ? ` in ${label}` : ''}`;
  }

  onSearchModalClick(event: MouseEvent): void {
    event.stopPropagation();
  }

  private loadSearchModalResults(force: boolean = false): void {
    if (!this.searchModalOpen && !force) {
      return;
    }

    const query = this.navbarSearchQuery.trim();
    if (query.length >= 3) {
      this.loadSearchApiResults(query);
      return;
    }

    if (query.length > 0 && query.length < 3) {
      this.searchModalResults = [];
      this.searchModalError = '';
      this.isSearchModalLoading = false;
      return;
    }

    this.loadCategoryModalResults();
  }

  private loadCategoryModalResults(): void {
    this.isSearchModalLoading = true;
    this.searchModalError = '';

    const city = this.selectedCity !== 'Choose city' ? this.selectedCity : '';
    const category = this.resolveSearchTabCategory(this.activeSearchTab);
    const eventsRequestParams =
      this.activeSearchTab === HEADER_SEARCH_ALL_TAB
        ? { ...(city ? { city } : {}), limit: 10 }
        : {
          category,
          ...(city ? { city } : {}),
          limit: 10
        };
    const eventsRequestUrl = this.buildDebugRequestUrl('events', eventsRequestParams);

    const request$ =
      this.dataService.get('events', eventsRequestParams);

    request$.subscribe({
      next: (response) => {
        if (this.activeSearchTab !== HEADER_SEARCH_ALL_TAB) {
          // console.log(`[Header Search] Decrypted response for ${eventsRequestUrl}:`, response);
        }
        const rows = this.extractEventRows(response)
          .filter((row) => this.matchesSelectedCity(row))
          .filter((row) => this.shouldShowSearchEvent(row))
          .slice(0, 10);
        this.searchModalResults = rows.map((row, index) => this.mapEventToSearchResult(row, index));
        this.isSearchModalLoading = false;
      },
      error: (err) => {
        this.isSearchModalLoading = false;
        this.searchModalResults = [];
        this.searchModalError = err?.error?.msg || err?.message || 'Failed to load search suggestions.';
      }
    });
  }

  private loadSearchApiResults(query: string): void {
    this.isSearchModalLoading = true;
    this.searchModalError = '';

    this.dataService.search(query, 'all').subscribe({
      next: (response) => {
        const rows = this.extractSearchRows(response)
          .filter((item) => this.shouldShowSearchEvent(item.raw))
          .filter((item) => this.matchesSearchTab(item, this.activeSearchTab))
          .slice(0, 10);
        this.searchModalResults = rows;
        this.isSearchModalLoading = false;
      },
      error: (err) => {
        this.isSearchModalLoading = false;
        this.searchModalResults = [];
        this.searchModalError = err?.error?.msg || err?.message || 'Failed to search right now.';
      }
    });
  }

  private extractEventRows(response: any): Array<Record<string, unknown>> {
    const data =
      response?.data?.data ??
      response?.data?.events ??
      response?.data ??
      response?.events ??
      response;
    if (Array.isArray(data)) {
      return data.filter((item): item is Record<string, unknown> => !!item && typeof item === 'object');
    }
    return [];
  }

  private extractSearchRows(response: any): HeaderSearchResult[] {
    const data = response?.data?.data ?? response?.data ?? response;

    if (Array.isArray(data)) {
      return data
        .filter((row): row is Record<string, unknown> => !!row && typeof row === 'object')
        .map((row, index) => this.mapEventToSearchResult(row, index));
    }

    const events = Array.isArray(data?.events) ? data.events : [];
    return events
      .filter((row: unknown): row is Record<string, unknown> => !!row && typeof row === 'object')
      .map((row: Record<string, unknown>, index: number) => this.mapEventToSearchResult(row, index));
  }

  private mapEventToSearchResult(row: Record<string, unknown>, index: number): HeaderSearchResult {
    const title = this.pickHeaderString(row, ['title', 'name', 'event_name', 'event_title']) || 'Untitled Event';
    const category = this.pickEventCategory(row) || 'Events';
    const city = this.pickHeaderString(row, ['city', 'city_name', 'location', 'venue_name']) || (this.selectedCity !== 'Choose city' ? this.selectedCity : 'City TBA');
    const parts = this.formatHeaderDateTime(row);
    return {
      id: `event-${String(row['id'] ?? row['reference_no'] ?? index)}`,
      kind: 'event',
      title,
      category,
      city,
      date: parts.date,
      time: parts.time,
      thumbnailImage: getEventPortraitImageUrl(row),
      thumbnailLabel: this.buildThumbnailLabel(title),
      thumbnailGradient: this.buildThumbnailGradient(title, index),
      raw: row,
      event_status: String(row['event_status'] ?? ''),
    };
  }

  private pickHeaderString(row: Record<string, unknown>, keys: string[]): string {
    for (const key of keys) {
      const value = row[key];
      if (typeof value === 'string' && value.trim()) {
        return value.trim();
      }
    }
    return '';
  }

  private pickEventCategory(row: Record<string, unknown>): string {
    const categories = row['categories'];
    if (Array.isArray(categories)) {
      for (const entry of categories) {
        if (typeof entry === 'string' && entry.trim()) {
          return entry.trim();
        }
        if (entry && typeof entry === 'object') {
          const label =
            this.pickHeaderString(entry as Record<string, unknown>, [
              'name',
              'label',
              'title',
              'category_name',
              'slug'
            ]);
          if (label) {
            return label;
          }
        }
      }
    }
    return this.pickHeaderString(row, ['category', 'event_type', 'type']);
  }

  private formatHeaderDateTime(row: Record<string, unknown>): { date: string; time: string } {
    const dateRaw = this.pickHeaderString(row, ['start_date', 'event_date', 'date', 'startDate']);
    if (!dateRaw) {
      return { date: 'Date TBA', time: 'Time TBA' };
    }
    const date = new Date(dateRaw);
    if (Number.isNaN(date.getTime())) {
      return { date: dateRaw, time: 'Time TBA' };
    }
    return {
      date: new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric', year: 'numeric' }).format(date),
      time: new Intl.DateTimeFormat('en-US', { hour: 'numeric', minute: '2-digit' }).format(date)
    };
  }

  private buildThumbnailLabel(title: string): string {
    return title
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((part) => part[0]?.toUpperCase() || '')
      .join('') || 'EV';
  }

  private buildThumbnailGradient(seed: string, index: number): string {
    const gradients = [
      'linear-gradient(135deg, #f97316 0%, #ec4899 100%)',
      'linear-gradient(135deg, #2563eb 0%, #7c3aed 100%)',
      'linear-gradient(135deg, #0f766e 0%, #06b6d4 100%)',
      'linear-gradient(135deg, #f59e0b 0%, #ef4444 100%)',
      'linear-gradient(135deg, #10b981 0%, #14b8a6 100%)',
      'linear-gradient(135deg, #1d4ed8 0%, #dc2626 100%)'
    ];
    const hash = Array.from(seed).reduce((sum, ch) => sum + ch.charCodeAt(0), index);
    return gradients[Math.abs(hash) % gradients.length];
  }

  private matchesSelectedCity(row: Record<string, unknown>): boolean {
    if (!this.selectedCity || this.selectedCity === 'Choose city') {
      return true;
    }
    const city = this.pickHeaderString(row, ['city', 'city_name', 'location', 'venue_name']).toLowerCase();
    return !city || city.includes(this.selectedCity.toLowerCase());
  }

  private matchesSearchTab(item: HeaderSearchSuggestion, tab: string): boolean {
    const active = tab.trim().toLowerCase();
    if (active === HEADER_SEARCH_ALL_TAB.toLowerCase()) {
      return true;
    }
    const category = item.category.trim().toLowerCase();
    return category === active || category === active.slice(0, -1) || category.includes(active.slice(0, -1));
  }

  private resolveSearchTabCategory(tab: string): string {
    if (tab === HEADER_SEARCH_ALL_TAB) {
      return '';
    }
    const normalized = tab.trim().toLowerCase();
    const match = this.categories.find((item) => {
      const name = this.getCategoryDisplayLabel(item).toLowerCase();
      const slug = String(item?.slug ?? '').trim().toLowerCase();
      return name === normalized || slug === normalized || name === normalized.slice(0, -1);
    });
    return String(match?.slug ?? match?.name ?? normalized).trim().toLowerCase();
  }

  private shouldShowSearchEvent(row: Record<string, unknown>): boolean {
    if (this.hasCompletedLikeStatus(row)) {
      return false;
    }

    const eventDate = this.resolveSearchEventDate(row);
    if (!eventDate) {
      return true;
    }

    return eventDate.getTime() >= Date.now();
  }

  private hasCompletedLikeStatus(row: Record<string, unknown>): boolean {
    const status = this.pickHeaderString(row, [
      'event_status',
      'eventStatus',
      'status',
      'show_status',
      'showStatus',
      'booking_status',
      'bookingStatus'
    ]).toLowerCase();

    if (!status) {
      return false;
    }

    return [
      'completed',
      'complete',
      'cancelled',
      'canceled',
      'ended',
      'closed',
      'expired'
    ].some((token) => status.includes(token));
  }

  private resolveSearchEventDate(row: Record<string, unknown>): Date | null {
    const candidates = [
      row['end_date'],
      row['endDate'],
      row['event_end_date'],
      row['eventEndDate'],
      row['start_date'],
      row['startDate'],
      row['event_date'],
      row['eventDate'],
      row['date']
    ];

    for (const candidate of candidates) {
      if (typeof candidate !== 'string' || !candidate.trim()) {
        continue;
      }

      const normalized = candidate.trim();
      if (/^\d{4}-\d{2}-\d{2}$/.test(normalized)) {
        const parsedDateOnly = new Date(`${normalized}T23:59:59`);
        if (!Number.isNaN(parsedDateOnly.getTime())) {
          return parsedDateOnly;
        }
      }

      const parsed = new Date(candidate);
      if (!Number.isNaN(parsed.getTime())) {
        return parsed;
      }
    }

    return null;
  }

  private buildDebugRequestUrl(endpoint: string, params?: Record<string, string | number>): string {
    const baseUrl = this.dataService.getUrl(endpoint);
    if (!params) {
      return baseUrl;
    }

    const searchParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== null && value !== undefined && String(value).trim() !== '') {
        searchParams.set(key, String(value));
      }
    });

    const query = searchParams.toString();
    return query ? `${baseUrl}?${query}` : baseUrl;
  }

  private clearSearchModalDebounce(): void {
    if (this.searchModalDebounceTimer) {
      clearTimeout(this.searchModalDebounceTimer);
      this.searchModalDebounceTimer = null;
    }
  }

  private addSearchResultToWishlist(referenceNo: string): void {
    this.wishlistService.addToWishlist(referenceNo).subscribe({
      next: () => {
        this.syncWishlistRefs();
        this.ms.add({
          severity: 'success',
          summary: 'Wishlist',
          detail: 'Event saved to wishlist.',
          life: 2500
        });
      },
      error: (err) => {
        this.ms.add({
          severity: 'error',
          summary: 'Wishlist',
          detail: err?.error?.msg || err?.error?.message || err?.message || 'Failed to save wishlist.',
          life: 3000
        });
      }
    });
  }

  private removeSearchResultFromWishlist(referenceNo: string): void {
    this.wishlistService.removeFromWishlist(referenceNo).subscribe({
      next: () => {
        this.syncWishlistRefs();
        this.ms.add({
          severity: 'success',
          summary: 'Wishlist',
          detail: 'Event removed from wishlist.',
          life: 2500
        });
      },
      error: (err) => {
        this.ms.add({
          severity: 'error',
          summary: 'Wishlist',
          detail: err?.error?.msg || err?.error?.message || err?.message || 'Failed to remove wishlist.',
          life: 3000
        });
      }
    });
  }

  private syncWishlistRefs(): void {
    if (!this.authService.isAuthenticated()) {
      this.wishlistService.clearWishlistRefs();
      return;
    }

    this.wishlistService.syncWishlistRefs(100, 1).subscribe({
      error: () => {
        // Keep header search usable even if wishlist sync fails.
      }
    });
  }

  toggleMobileMenu() {
    this.mobileMenuOpen = !this.mobileMenuOpen;
    this.userDropdownOpen = false;
    if (this.mobileMenuOpen) {
      document.body.classList.add('mobile-menu-open');
    } else {
      document.body.classList.remove('mobile-menu-open');
    }
  }

  closeMobileMenu() {
    this.mobileMenuOpen = false;
    document.body.classList.remove('mobile-menu-open');
  }

  readonly LIST_EVENT_URL = 'https://preprod-organiser.livepass.in/login';

  navigateToOrganizer() {
    window.open(this.LIST_EVENT_URL, '_blank');
  }

  loadCategories() {
    this.fetchAllHeaderCategoriesPage(0, []);
  }

  private fetchAllHeaderCategoriesPage(offset: number, collected: any[]): void {
    this.dataService.getCategories(HEADER_CATEGORY_PAGE_SIZE, offset).subscribe({
      next: (response) => {
        const pageItems = this.parseHeaderCategories(response);
        const merged = [...collected, ...pageItems];

        if (pageItems.length === HEADER_CATEGORY_PAGE_SIZE) {
          this.fetchAllHeaderCategoriesPage(offset + HEADER_CATEGORY_PAGE_SIZE, merged);
          return;
        }

        this.finalizeHeaderCategories(merged);
      },
      error: (err) => {
        // console.error('Error loading categories:', err);
        this.finalizeHeaderCategories(collected);
      }
    });
  }

  private parseHeaderCategories(response: any): any[] {
    if (response && response.code === 1 && response.data) {
      if (response.data.code === 1 && Array.isArray(response.data.data)) {
        return response.data.data;
      }
      if (Array.isArray(response.data)) {
        return response.data;
      }
    } else if (Array.isArray(response)) {
      return response;
    } else if (response && Array.isArray(response.data)) {
      return response.data;
    }
    return [];
  }

  private finalizeHeaderCategories(categories: any[]): void {
    this.categories = this.dedupeHeaderCategories(categories);
    this.searchTabs = this.buildDynamicSearchTabs(this.categories);

    if (!this.searchTabs.includes(this.activeSearchTab)) {
      this.activeSearchTab = HEADER_SEARCH_ALL_TAB;
    }
  }

  private dedupeHeaderCategories(categories: any[]): any[] {
    const seen = new Set<string>();
    return categories.filter((item) => {
      const key = String(item?.id ?? item?.slug ?? this.getCategoryDisplayLabel(item)).trim().toLowerCase();
      if (!key || seen.has(key)) {
        return false;
      }
      seen.add(key);
      return true;
    });
  }

  private buildDynamicSearchTabs(categories: any[]): string[] {
    const tabs = [HEADER_SEARCH_ALL_TAB];
    const seen = new Set<string>([HEADER_SEARCH_ALL_TAB.toLowerCase()]);

    for (const item of categories) {
      const label = this.getCategoryDisplayLabel(item);
      const key = label.toLowerCase();
      if (!label || seen.has(key)) {
        continue;
      }
      seen.add(key);
      tabs.push(label);
    }

    return tabs;
  }

  private getCategoryDisplayLabel(category: any): string {
    const raw = String(category?.name ?? category?.label ?? category?.slug ?? '').trim();
    if (!raw) {
      return '';
    }

    return raw
      .replace(/[-_]+/g, ' ')
      .replace(/\s+/g, ' ')
      .split(' ')
      .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
      .join(' ');
  }

  private loadNavbar(): void {
    this.dataService.get<any>('navbar').subscribe({
      next: (response) => {
        // Log decrypted payload as requested
        // console.log('[Navbar API] response:', response);

        const raw =
          response?.data?.data ??
          response?.data?.items ??
          response?.data ??
          response?.items ??
          response;

        const items = Array.isArray(raw) ? raw : [];
        if (!items.length) {
          return;
        }

        const mapped = items
          .map((it: any) => {
            const label = (it?.name ?? '').toString().trim();
            if (!label) return null;

            const lower = label.toLowerCase();
            const href =
              (it?.href ?? it?.url ?? it?.link ?? it?.path ?? '').toString().trim() || undefined;

            if (lower === 'discover' || lower === 'home') {
              return { label: label === 'Home' ? 'Discover' : label, kind: 'discover' as const };
            }

            // If API gives explicit "category" semantics, treat as category; otherwise treat as category by name.
            const categoryName =
              (it?.category ?? it?.categoryName ?? it?.category_name ?? it?.categoryLabel ?? '').toString().trim();

            if (categoryName) {
              return { label, kind: 'category' as const, categoryName };
            }

            return { label, kind: 'category' as const, categoryName: label, href };

          })
          .filter(Boolean) as HeaderComponent['headerNavItems'];

        if (mapped.length) {
          this.headerNavItems = mapped;
        }
      },
      error: (err) => {
        // console.error('[Navbar API] error:', err);
      }
    });
  }

  onNavClick(event: Event, item: HeaderComponent['headerNavItems'][number]): void {
    if (!item) return;
    if (item.kind === 'discover') {
      this.navigateToDiscover(event);
      return;
    }
    if (item.kind === 'category') {
      event.preventDefault();
      void this.router.navigate(['/discover'], {
        queryParams: { category: (item.categoryName || item.label).toLowerCase() }
      });
      return;
    }
    // kind=link
    if (!item.href) {
      // No href provided; fall back to Discover browse
      this.navigateToDiscover(event);
      return;
    }
    const href = item.href.trim();
    if (href.startsWith('/')) {
      event.preventDefault();
      void this.router.navigateByUrl(href);
      return;
    }
    // external / absolute link: allow default navigation
  }

  isNavItemActive(item: HeaderComponent['headerNavItems'][number]): boolean {
    if (!item) return false;
    if (item.kind === 'discover') return this.isDiscoverNavActive();
    if (item.kind === 'category') {
      const path = this.router.url.split('?')[0] || '';
      if (path !== '/discover') return false;
      const tree = this.router.parseUrl(this.router.url);
      const q = tree.queryParams;
      const qCat = (q['category'] || '').toString().toLowerCase();
      const want = (item.categoryName || item.label).toLowerCase();
      if (qCat && qCat === want) return true;
      return this.isCategoryNavActive(item.categoryName || item.label);
    }
    return false;
  }

  navigateToHome(event: Event) {
    const url = this.router.url.split('?')[0];
    if (url === '/' || url === '') {
      event.preventDefault();
    }
  }

  navigateToDiscover(event: Event) {
    event.preventDefault();
    void this.router.navigateByUrl('/');
  }

  /** Home (`/`) or plain Discover browse (`/discover` without category filters). */
  isDiscoverNavActive(): boolean {
    const path = this.router.url.split('?')[0] || '/';
    if (path === '/' || path === '') {
      return true;
    }
    if (path !== '/discover') {
      return false;
    }
    const tree = this.router.parseUrl(this.router.url);
    const q = tree.queryParams;
    if (q['category'] || q['category_id'] || q['category_ids']) {
      return false;
    }
    return true;
  }

  /** True when `/discover` matches this top nav category (Concerts, Expos, Conferences). */
  isCategoryNavActive(navLabel: string): boolean {
    const path = this.router.url.split('?')[0] || '';
    if (path !== '/discover') {
      return false;
    }
    const tree = this.router.parseUrl(this.router.url);
    const q = tree.queryParams;
    if (q['category_ids']) {
      return false;
    }

    const qCat = (q['category'] || '').toString().toLowerCase();
    const qId = (q['category_id'] || '').toString();

    const match = this.categories.find(
      (c) =>
        (c.name || '').toLowerCase() === navLabel.toLowerCase() ||
        (c.label || '').toLowerCase() === navLabel.toLowerCase() ||
        (c.slug || '').toLowerCase() === navLabel.toLowerCase()
    );

    if (qId && match?.id != null) {
      return String(match.id) === qId;
    }
    if (!qCat) {
      return false;
    }

    if (match) {
      const nameVal = (match.name || match.label || match.slug || navLabel).toLowerCase();
      const slugVal = (match.slug || '').toLowerCase();
      return qCat === nameVal || qCat === slugVal || qCat === navLabel.toLowerCase();
    }
    return qCat === navLabel.toLowerCase();
  }

  navigateToCategory(event: Event, categoryName: string) {
    event.preventDefault();
    // Find category by name (Concerts, Expos, Conferences) - use category name for search
    const category = this.categories.find(cat =>
      cat.name?.toLowerCase() === categoryName.toLowerCase() ||
      cat.label?.toLowerCase() === categoryName.toLowerCase() ||
      cat.slug?.toLowerCase() === categoryName.toLowerCase()
    );

    // Use category name from API if found, else use the link name - always lowercase in URL
    const value = category ? (category.name || category.label || category.slug) : categoryName;
    if (value) {
      this.router.navigate(['/discover'], { queryParams: { category: value.toLowerCase() } });
    }
  }

  loadUserProfile() {
    // Only call API for logged-in users
    if (!this.authService.isAuthenticated()) {
      return;
    }
    this.loginService.getUserProfile().subscribe({
      next: (response) => {
        const data = response?.data?.user ?? response?.data?.data ?? response?.data ?? response?.user ?? response;
        if (data && typeof data === 'object') {
          this.userProfile = data;
          // console.log(data);

          this.authService.setUserProfile(data);
        }
      },
      error: (error) => {
        // console.error('Error loading user profile:', error);
        // If 401 Unauthorized, user is not logged in - clear auth
        if (error?.status === 401) {
          this.authService.clearAuth();
          this.userProfile = null;
        }
      }
    });
  }

  toggleUserDropdown() {
    this.userDropdownOpen = !this.userDropdownOpen;
  }

  closeUserDropdown() {
    this.userDropdownOpen = false;
  }

  logout() {
    this.closeLogoutModal();
    this.isLoading = true;
    this.userDropdownOpen = false;
    this.mobileMenuOpen = false;
    document.body.classList.remove('mobile-menu-open');

    this.loginService.logout().subscribe({
      next: (response) => {
        // console.log('Logout successful:', response);
        this.authService.clearAuth();
        this.userProfile = null;
        this.isLoggedIn = false;
        this.isLoading = false;
        this.ms.add({
          severity: 'success',
          summary: 'Success',
          detail: 'Logout successful',
          life: 2000
        });

        void this.router.navigateByUrl('/');
      },
      error: (error) => {
        // console.error('Error during logout:', error);
        // Clear auth data even if API call fails
        this.authService.clearAuth();
        this.userProfile = null;
        this.isLoggedIn = false;
        this.isLoading = false;
        void this.router.navigateByUrl('/');
      }
    });
  }

  /** Use initials only when there is a real name — never from phone/email. */
  hasNameBasedAvatar(): boolean {
    const p = this.userProfile;
    if (!p) return false;
    const first = (p.first_name || '').trim();
    const last = (p.last_name || '').trim();
    if (first || last) return true;
    if ((p.full_name || '').trim()) return true;
    if ((p.name || '').trim()) return true;
    return false;
  }

  getUserInitials(): string {
    const first = (this.userProfile?.first_name || '').trim();
    const last = (this.userProfile?.last_name || '').trim();
    if (first && last) {
      return `${first[0]}${last[0]}`.toUpperCase();
    }
    if (first || last) {
      const single = first || last;
      return single.length >= 2
        ? `${single[0]}${single[1]}`.toUpperCase()
        : single[0].toUpperCase();
    }

    if (this.userProfile?.full_name) {
      const fullName = this.userProfile.full_name.trim();
      if (fullName) {
        const names = fullName.split(' ').filter(n => n.length > 0);
        if (names.length >= 2) {
          return `${names[0][0]}${names[1][0]}`.toUpperCase();
        }
        if (names.length === 1) {
          const w = names[0];
          return w.length >= 2 ? `${w[0]}${w[1]}`.toUpperCase() : w[0].toUpperCase();
        }
      }
    }

    if (this.userProfile?.name) {
      const name = this.userProfile.name.trim();
      if (name) {
        const parts = name.split(/\s+/).filter(Boolean);
        if (parts.length >= 2) {
          return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
        }
        return name.length >= 2 ? `${name[0]}${name[1]}`.toUpperCase() : name[0].toUpperCase();
      }
    }

    return '';
  }

  getUserDisplayName(): string {
    if (this.userProfile?.full_name) {
      const fullName = this.userProfile.full_name.trim();
      if (fullName) return fullName;
    }

    if (this.userProfile?.first_name || this.userProfile?.last_name) {
      const first = (this.userProfile.first_name || '').trim();
      const last = (this.userProfile.last_name || '').trim();
      if (first || last) {
        return `${first} ${last}`.trim();
      }
    }

    if (this.userProfile?.name) {
      const name = this.userProfile.name.trim();
      if (name) return name;
    }

    return '';
  }

  /** Second line in user menu: contact hint, not used as display name. */
  getUserAccountSubtitle(): string {
    const p = this.userProfile;
    if (!p) return '—';
    const phone = (p.phone || '').trim();
    if (phone) {
      const digits = phone.replace(/\D/g, '');
      const local = digits.startsWith('91') && digits.length >= 10 ? digits.slice(-10) : digits.slice(-10) || digits;
      if (local.length === 10) {
        return `+91 ${local.replace(/(\d{5})(\d{5})/, '$1 $2')}`;
      }
      return phone;
    }
    if ((p.email || '').trim()) return p.email!.trim();
    return '—';
  }

  openLoginModal() {
    this.loginModalOpen = true;
    this.loginModalService.setModalOpen(true);
    this.resetForm();
    this.syncBodyModalScrollLock();
  }

  closeLoginModal() {
    this.loginModalOpen = false;
    this.loginModalService.setModalOpen(false);
    this.resetForm();
    this.clearTimer();
    this.syncBodyModalScrollLock();
  }

  openLocationUpdateModal(fromCity: string, toCity: string): void {
    this.locationUpdateFrom = fromCity;
    this.locationUpdateTo = toCity;
    this.locationUpdateModalOpen = true;
    this.syncBodyModalScrollLock();
  }

  closeLocationUpdateModal(): void {
    this.locationUpdateModalOpen = false;
    this.locationUpdateFrom = '';
    this.locationUpdateTo = '';
    this.syncBodyModalScrollLock();
  }

  openLogoutModal(): void {
    this.userDropdownOpen = false;
    this.logoutModalOpen = true;
    this.syncBodyModalScrollLock();
  }

  closeLogoutModal(): void {
    this.logoutModalOpen = false;
    this.syncBodyModalScrollLock();
  }

  onLogoutModalClick(event: MouseEvent): void {
    const target = event.target as HTMLElement;
    if (target.classList.contains('login-modal-overlay')) {
      this.closeLogoutModal();
    }
  }

  onLocationUpdateModalClick(event: MouseEvent): void {
    const target = event.target as HTMLElement;
    if (target.classList.contains('login-modal-overlay')) {
      this.closeLocationUpdateModal();
    }
  }

  private syncBodyModalScrollLock(): void {
    if (this.searchModalOpen || this.loginModalOpen || this.locationUpdateModalOpen || this.logoutModalOpen) {
      document.body.classList.add('modal-open');
    } else {
      document.body.classList.remove('modal-open');
    }
  }

  /** Close login modal then open Terms or Privacy in the same tab. */
  openLegalFromModal(path: '/terms' | '/privacy', event: Event): void {
    event.preventDefault();
    event.stopPropagation();
    this.closeLoginModal();
    void this.router.navigateByUrl(path);
  }

  resetForm() {
    this.loginModalStep = 'mobile';
    this.loginEmail = '';
    this.loginPassword = '';
    this.mobileNumber = '';
    this.selectedCountryId = 1;
    this.errorMessage = '';
    this.successMessage = '';
    this.otpSent = false;
    this.otpVerificationToken = '';
    this.otpExpiry = 0;
    this.otpDigits = ['', '', '', '']; // 4-digit OTP
    this.otpTimer = 0;
    this.otpExpired = false;
    this.isLoading = false;
  }

  private resetOtpFlow(): void {
    this.otpSent = false;
    this.otpVerificationToken = '';
    this.otpExpiry = 0;
    this.otpDigits = ['', '', '', ''];
    this.otpTimer = 0;
    this.otpExpired = false;
    this.clearTimer();
  }

  /** From mobile/OTP screen: show email + password form */
  switchToEmailLogin(): void {
    this.loginModalStep = 'email';
    this.errorMessage = '';
    this.successMessage = '';
    this.isLoading = false;
    this.loginEmail = '';
    this.loginPassword = '';
    this.resetOtpFlow();
    setTimeout(() => {
      const el = document.getElementById('login-email') as HTMLInputElement;
      el?.focus();
    }, 100);
  }

  /** From email screen: back to mobile number + OTP flow */
  switchToMobileOtpLogin(): void {
    this.loginModalStep = 'mobile';
    this.errorMessage = '';
    this.successMessage = '';
    this.isLoading = false;
    this.loginEmail = '';
    this.loginPassword = '';
    this.resetOtpFlow();
    setTimeout(() => {
      const el = document.getElementById('mobile') as HTMLInputElement;
      el?.focus();
    }, 100);
  }

  emailLooksValid(): boolean {
    const e = this.loginEmail.trim();
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e);
  }

  signInWithEmail(): void {
    this.errorMessage = '';
    this.successMessage = '';
    if (!this.emailLooksValid()) {
      this.errorMessage = 'Please enter a valid email address.';
      return;
    }
    if (!this.loginPassword) {
      this.errorMessage = 'Please enter your password.';
      return;
    }

    this.isLoading = true;
    this.loginModalService.emailLogin(this.loginEmail.trim(), this.loginPassword).subscribe({
      next: (response) => {
        this.isLoading = false;
        const d = response?.data ?? response;
        let loggedIn = this.authService.applyAuthApiResult(response);

        if (!loggedIn && response?.code === 1) {
          const tok =
            response?.token ??
            (typeof response?.access_token === 'string' ? response.access_token : null) ??
            d?.token ??
            (typeof d?.access_token === 'string' ? d.access_token : null);
          if (tok) {
            this.authService.setToken(tok);
            loggedIn = true;
          }
        }

        if (loggedIn || this.authService.getToken()) {
          // Token may be available immediately, but profile fetch is async.
          // Flip UI state right away so header doesn't keep showing "Sign In".
          this.isLoggedIn = true;


          this.successMessage = response?.msg ?? response?.message ?? d?.msg ?? 'Login successful!';
          const rawId = response?.userId ?? response?.user_id ?? d?.userId ?? d?.user_id;
          if (rawId != null && rawId !== '') {
            const numId = typeof rawId === 'number' ? rawId : parseInt(String(rawId), 10);
            if (!Number.isNaN(numId)) {
              this.authService.setUserId(numId);
            }
          }
          const userData =
            response?.data?.user ?? response?.data ?? response?.user ?? d?.user ?? (response as any)?.result?.user;
          if (userData && typeof userData === 'object' && !Array.isArray(userData)) {
            this.authService.setUserProfile(userData);
            this.userProfile = userData;
          }
          this.loadUserProfile();
          this.cartService.getCart().subscribe({ next: () => { }, error: () => { } });

          const pendingAction = this.loginModalService.consumePendingAction();
          if (pendingAction) {
            pendingAction();
          } else {
            void this.router.navigateByUrl('/');
          }
          setTimeout(() => {
            this.closeLoginModal();
          }, 1500);
        } else {
          this.errorMessage =
            response?.msg ||
            response?.message ||
            d?.msg ||
            'Login failed. Please check your credentials.';
        }
      },
      error: (error) => {
        this.isLoading = false;
        this.errorMessage =
          error.error?.msg ||
          error.error?.message ||
          error.message ||
          'Login failed. Please try again.';
        console.error('Email login error:', error);
      }
    });
  }

  onModalClick(event: MouseEvent) {
    // const target = event.target as HTMLElement;
    // if (target.classList.contains('login-modal-overlay')) {
    //   this.closeLoginModal();
    // }
  }

  sendOtp() {
    if (this.isLoading) return;
    if (!this.isMobileNumberValid) {
      this.errorMessage = this.mobileNumberValidationMessage;
      return;
    }

    this.isLoading = true;
    this.errorMessage = '';
    this.successMessage = '';

    this.loginService.sendOtp(this.mobileNumber, this.selectedCountryCode).subscribe({
      next: (response) => {
        this.isLoading = false;
        const d = response?.data ?? response;
        const token =
          response?.otpVerificationToken ??
          response?.otp_verification_token ??
          d?.otpVerificationToken ??
          d?.otp_verification_token;
        const expiry =
          response?.otpExpiry ??
          response?.otp_expiry ??
          d?.otpExpiry ??
          d?.otp_expiry;

        if (token) {
          this.otpSent = true;
          this.otpVerificationToken = token;
          this.otpExpiry = typeof expiry === 'number' && expiry > 0 ? expiry : 120;
          this.successMessage =
            response?.msg ?? response?.message ?? d?.msg ?? 'OTP sent successfully! Please check your mobile.';
          this.startTimer();
          setTimeout(() => {
            const firstInput = document.querySelector('.otp-input') as HTMLInputElement;
            firstInput?.focus();
          }, 100);
        } else {
          this.errorMessage =
            response?.msg ||
            response?.message ||
            d?.msg ||
            d?.message ||
            'Failed to send OTP. Please try again.';
        }
      },
      error: (error) => {
        this.isLoading = false;
        this.errorMessage = error.error?.msg || error.error?.message || 'Failed to send OTP. Please try again.';
        // console.error('Error sending OTP:', error);
      }
    });
  }

  onMobileNumberInput(value: string): void {
    this.mobileNumber = value || '';
    this.errorMessage = this.mobileNumber && !this.isMobileNumberValid
      ? this.mobileNumberValidationMessage : '';
    this.successMessage = '';
  }

  onMobileNumberKeyDown(event: KeyboardEvent): void {
    const allowed = [
      'Backspace',
      'Delete',
      'Tab',
      'Enter',
      'Escape',
      'ArrowLeft',
      'ArrowRight',
      'Home',
      'End'
    ];
    if (allowed.includes(event.key)) return;
    if (event.ctrlKey || event.metaKey) return; // allow copy/paste/select all shortcuts
    if (/^\d$/.test(event.key)) return;
    event.preventDefault();
  }

  startTimer() {
    this.otpExpired = false;
    this.otpTimer = this.otpExpiry;
    this.timerInterval = setInterval(() => {
      this.otpTimer--;
      if (this.otpTimer <= 0) {
        this.otpTimer = 0;
        this.otpExpired = true;
        this.successMessage = '';
        this.errorMessage = '';
        this.clearTimer();
      }
    }, 1000);
  }

  clearTimer() {
    if (this.timerInterval) {
      clearInterval(this.timerInterval);
      this.timerInterval = null;
    }
  }

  formatTimer(seconds: number): string {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  }

  resendOtp() {
    this.otpDigits = ['', '', '', '']; // 4-digit OTP
    this.errorMessage = '';
    this.successMessage = '';
    this.sendOtp();
  }

  changeMobileNumber() {
    this.otpSent = false;
    this.otpDigits = ['', '', '', '']; // 4-digit OTP
    this.errorMessage = '';
    this.successMessage = '';
    this.otpExpired = false;
    this.clearTimer();
    setTimeout(() => {
      const mobileInput = document.getElementById('mobile') as HTMLInputElement;
      if (mobileInput) {
        mobileInput.focus();
      }
    }, 100);
  }

  onOtpInput(index: number, event: any) {
    const value = event.target.value.replace(/\D/g, ''); // Only numbers
    if (value.length > 1) {
      // If pasting multiple digits
      const digits = value.split('').slice(0, 4); // 4-digit OTP
      digits.forEach((digit: string, i: number) => {
        if (index + i < 4) {
          this.otpDigits[index + i] = digit;
        }
      });
      // Focus last input
      const lastIndex = Math.min(index + digits.length - 1, 3);
      const inputs = document.querySelectorAll('.otp-input');
      if (inputs[lastIndex]) {
        (inputs[lastIndex] as HTMLInputElement).focus();
      }
    } else {
      this.otpDigits[index] = value;

      // Auto-focus next input
      if (value && index < 3) { // 4-digit OTP (indices 0-3)
        const inputs = document.querySelectorAll('.otp-input');
        if (inputs[index + 1]) {
          (inputs[index + 1] as HTMLInputElement).focus();
        }
      }
    }
  }

  onOtpKeyDown(index: number, event: KeyboardEvent) {

    if (event.key === 'Enter') {
      event.preventDefault();
      this.verifyOtp();
      return;
    }

    // Handle backspace
    if (event.key === 'Backspace' && !this.otpDigits[index] && index > 0) {
      const inputs = document.querySelectorAll('.otp-input');
      (inputs[index - 1] as HTMLInputElement).focus();
    }
  }

  verifyOtp() {
    const otp = this.otpDigits.join('');

    if (this.otpExpired) {
      return;
    }

    if (otp.length !== 4) {
      this.errorMessage = 'Please enter complete 4-digit OTP';
      return;
    }

    if (!this.otpVerificationToken) {
      this.errorMessage = 'OTP verification token missing. Please request OTP again.';
      return;
    }

    this.isLoading = true;
    this.errorMessage = '';
    this.successMessage = '';

    this.loginService.verifyOtp(otp, this.otpVerificationToken).subscribe({
      next: (response) => {
        this.isLoading = false;
        // console.log('[verify-otp/email] Decrypted response:', response);
        const d = response?.data ?? response;
        let loggedIn = this.authService.applyAuthApiResult(response);

        if (!loggedIn && response?.code === 1) {
          const tok =
            response?.token ??
            (typeof response?.access_token === 'string' ? response.access_token : null) ??
            d?.token ??
            (typeof d?.access_token === 'string' ? d.access_token : null);
          if (tok) {
            this.authService.setToken(tok);
            loggedIn = true;

          }
        }

        if (loggedIn || this.authService.getToken()) {
          // Token may be available immediately, but profile fetch is async.
          // Flip UI state right away so header doesn't keep showing "Sign In".
          this.ms.add({
            severity: 'success',
            summary: 'Success',
            detail: 'Login successful',
            life: 2000
          });
          this.isLoggedIn = true;
          this.successMessage = response?.msg ?? response?.message ?? d?.msg ?? 'Login successful!';
          const rawId = response?.userId ?? response?.user_id ?? d?.userId ?? d?.user_id;
          if (rawId != null && rawId !== '') {
            const numId = typeof rawId === 'number' ? rawId : parseInt(String(rawId), 10);
            if (!Number.isNaN(numId)) {
              this.authService.setUserId(numId);
            }
          }
          const userData =
            response?.data?.user ?? response?.data ?? response?.user ?? d?.user ?? (response as any)?.result?.user;
          if (userData && typeof userData === 'object' && !Array.isArray(userData)) {
            this.authService.setUserProfile(userData);
            this.userProfile = userData;
          }
          this.loadUserProfile();
          this.cartService.getCart().subscribe({ next: () => { }, error: () => { } });

          const pendingAction = this.loginModalService.consumePendingAction();
          if (pendingAction) {
            pendingAction();
          } else {
            void this.router.navigateByUrl('/');
          }
          setTimeout(() => {
            this.closeLoginModal();
          }, 1500);
        } else {
          this.errorMessage =
            response?.msg ||
            response?.message ||
            d?.msg ||
            d?.message ||
            'Invalid OTP or login failed. Please try again.';
        }
      },
      error: (error) => {
        this.isLoading = false;
        // console.log('[verify-otp/email] Decrypted error response:', error?.error ?? error);
        this.errorMessage = error.error?.msg || error.error?.message || 'Invalid OTP. Please try again.';
        console.error('Error verifying OTP:', error);
        // Clear OTP on error
        this.otpDigits = ['', '', '', '']; // 4-digit OTP
        const firstInput = document.querySelector('.otp-input') as HTMLInputElement;
        if (firstInput) {
          firstInput.focus();
        }
      }
    });
  }

  private startReviewCountdownTicker(): void {
    const update = () => {
      const raw = Number(sessionStorage.getItem('review_expiry_at') ?? '');
      if (!Number.isFinite(raw) || raw <= 0) {
        this.reviewCountdown = '';
        this.reviewCountdownDanger = false;
        this.reviewExpiryHandled = false;
        return;
      }
      const remainingMs = raw - Date.now();
      if (remainingMs <= 0) {
        this.reviewCountdown = '';
        this.reviewCountdownDanger = false;
        if (!this.reviewExpiryHandled) {
          this.reviewExpiryHandled = true;
          try {
            sessionStorage.removeItem('review_expiry_at');
          } catch {
            /* ignore */
          }
          void this.seatHoldRelease.releaseIfUnbooked().then(() => {
            this.cartService.setCartHasItems(false);
            if (this.authService.isAuthenticated()) {
              this.cartService.clearAllCart().subscribe({ next: () => { }, error: () => { } });
            }
          });
        }
        return;
      }
      // New/active timer — allow expiry handler to run again for next session.
      this.reviewExpiryHandled = false;
      const totalSecs = Math.floor(remainingMs / 1000);
      const mins = Math.floor(totalSecs / 60);
      const secs = totalSecs % 60;
      this.reviewCountdown = `${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
      this.reviewCountdownDanger = totalSecs <= 3 * 60;
    };
    update();
    this.reviewTimerInterval = setInterval(update, 1000);
  }
}
